/**
 * Gemi kütüphanesinin metinleri (M8a4e; ADR 0036, ADR 0038): gemide yükleme, yeni revizyon ve
 * kaptan onayı; ofiste kütüphane seçici, salt okunur gemi kütüphanesi ve dosyaların yükleme
 * durumu. `documents.ts` bunları `documents.library` altına katar.
 */

export const documentsLibraryEn = {
  company: 'Company',
  companyHint: 'Company document – read only on board.',
  draftOnVessel: 'Draft (on vessel)',
  selfApproved: 'Self-approved',
  selfApprovedHint:
    'Approved by the person who uploaded or submitted this revision, under the approval matrix exception and its recorded reason: the DPA or company administrator in the office (ISM 11.2.2), the master on board (ISM Code 5). The reason is kept in the audit trail.',
  readOnly:
    'This is how the vessel sees its library. Read only: revise documents in Company documents.',
  // ADR 0081'den önce gemide doğmuş belge
  legacyVessel:
    'The vessel’s own document, added on board before only the company published documents. It cannot be revised on board; the company publishes the controlled version.',
  legacyOffice:
    'The vessel’s own document, added on board before only the company published documents. To control it, add it in Company documents with Distribution: Selected vessels.',
  openCompany: 'Open company documents',
  retiredFolder:
    'This document is in a retired folder. Move it to a live folder before uploading a new revision.',
  picker: {
    label: 'Library',
    company: 'Company documents',
    vessel: '{{name}} library',
    vessels: 'Vessel libraries',
  },
  upload: {
    available: 'In office',
    uploading: 'On vessel – uploading {{percent}}%',
    verifying: 'On vessel – verifying in office',
    waiting: 'On vessel – waiting for upload',
    waitingWindow: 'On vessel – waiting for upload window',
    waitingCap: 'On vessel – monthly transfer limit reached',
    waitingPaused: 'On vessel – uploads paused',
    waitingManual: 'On vessel – waiting for manual upload',
    waitingBlocked: 'On vessel – upload blocked',
    onVessel: 'On vessel',
    failed: 'Upload failed',
  },
  marker: {
    waiting: 'Waiting to upload',
    uploading: 'Uploading',
    failed: 'Upload failed',
    inOffice: 'In office',
  },
  summary: {
    title: 'Uploads from vessel',
    pending: '{{count}} files waiting ({{size}})',
    failed: '{{count}} failed',
    notReported: 'Not reported yet',
    states: {
      idle: 'Up to date',
      uploading: 'Uploading',
      window: 'Waiting for upload window',
      cap: 'Monthly limit reached',
      paused: 'Paused',
      manual: 'Manual uploads only',
      offline: 'Offline or USB',
      blocked: 'Blocked',
    },
  },
  notUploaded: {
    title: 'The file is still on the vessel',
    description:
      'The office can open and download it after the vessel uploads it and the office verifies it.',
    status: 'Status',
    size: 'Size',
  },
  shipUploads: {
    title: 'Uploads to office',
    pendingFiles: 'Waiting files',
    pendingBytes: 'Waiting data',
    failed: 'Failed',
    state: 'Status',
  },
  drawer: {
    fileHint: 'Word, Excel, PDF, JPG or PNG; up to 1 GB. The file is uploaded from this computer.',
    choose: 'Choose file',
    addAttachments: 'Add files',
    chooseTitle: 'Choose the document file',
    attachmentsTitle: 'Choose attachments',
  },
};

export const documentsLibraryTr: typeof documentsLibraryEn = {
  company: 'Şirket',
  companyHint: 'Şirket dokümanı – gemide salt okunur.',
  draftOnVessel: 'Taslak (gemide)',
  selfApproved: 'Kendi onayı',
  selfApprovedHint:
    'Revizyonu yükleyen ya da gönderen kişi onayladı; onay matrisinin istisnası ve kayıtlı gerekçesiyle: ofiste DPA ya da şirket yöneticisi (ISM 11.2.2), gemide kaptan (ISM Kodu 5). Gerekçe denetim kaydında saklanır.',
  readOnly:
    'Geminin kütüphanesini gördüğü gibi. Salt okunur: dokümanları Şirket dokümanları’nda revize edin.',
  legacyVessel:
    'Geminin kendi belgesi; dokümanı yalnız şirket yayınlamadan önce gemide eklendi. Gemide revize edilemez; denetimli sürümü şirket yayınlar.',
  legacyOffice:
    'Geminin kendi belgesi; dokümanı yalnız şirket yayınlamadan önce gemide eklendi. Denetim altına almak için Şirket dokümanları’nda Dağıtım: Seçili gemiler ile ekleyin.',
  openCompany: 'Şirket dokümanlarını aç',
  retiredFolder:
    'Bu doküman emekli bir klasörde. Yeni revizyon yüklemeden önce canlı bir klasöre taşıyın.',
  picker: {
    label: 'Kütüphane',
    company: 'Şirket dokümanları',
    vessel: '{{name}} kütüphanesi',
    vessels: 'Gemi kütüphaneleri',
  },
  upload: {
    available: 'Ofiste',
    uploading: 'Gemide – yükleniyor %{{percent}}',
    verifying: 'Gemide – ofiste doğrulanıyor',
    waiting: 'Gemide – yüklenmeyi bekliyor',
    waitingWindow: 'Gemide – yükleme penceresini bekliyor',
    waitingCap: 'Gemide – aylık aktarım sınırı doldu',
    waitingPaused: 'Gemide – yüklemeler duraklatıldı',
    waitingManual: 'Gemide – elle yüklemeyi bekliyor',
    waitingBlocked: 'Gemide – yükleme engellendi',
    onVessel: 'Gemide',
    failed: 'Yükleme başarısız',
  },
  marker: {
    waiting: 'Yüklenmeyi bekliyor',
    uploading: 'Yükleniyor',
    failed: 'Yükleme başarısız',
    inOffice: 'Ofiste',
  },
  summary: {
    title: 'Gemiden yüklemeler',
    pending: '{{count}} dosya bekliyor ({{size}})',
    failed: '{{count}} başarısız',
    notReported: 'Henüz bildirilmedi',
    states: {
      idle: 'Güncel',
      uploading: 'Yükleniyor',
      window: 'Yükleme penceresini bekliyor',
      cap: 'Aylık sınır doldu',
      paused: 'Duraklatıldı',
      manual: 'Yalnız elle yükleme',
      offline: 'İnternetsiz ya da USB',
      blocked: 'Engellendi',
    },
  },
  notUploaded: {
    title: 'Dosya henüz gemide',
    description: 'Gemi dosyayı yükleyip ofis doğruladıktan sonra ofiste açılır ve indirilir.',
    status: 'Durum',
    size: 'Boyut',
  },
  shipUploads: {
    title: 'Ofise yüklemeler',
    pendingFiles: 'Bekleyen dosya',
    pendingBytes: 'Bekleyen veri',
    failed: 'Başarısız',
    state: 'Durum',
  },
  drawer: {
    fileHint: 'Word, Excel, PDF, JPG ya da PNG; en çok 1 GB. Dosya bu bilgisayardan yüklenir.',
    choose: 'Dosya seç',
    addAttachments: 'Dosya ekle',
    chooseTitle: 'Doküman dosyasını seçin',
    attachmentsTitle: 'Ekleri seçin',
  },
};
